import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {
    OptionItem,
    SeparatorItem,
    SingleSelect,
} from "@khanacademy/wonder-blocks-dropdown";
import {Strut} from "@khanacademy/wonder-blocks-layout";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";
import {usePublishAnalyticsEvent} from "@khan/analytics-learning-time";
import {useRegionOverride} from "@khan/data-region-override";
import {doNotTranslateYet} from "@khan/i18n";
import {RequestInfo} from "@khan/shared-core";

import {
    getSortedRegionPickerOptions,
    useRegionPickerQuery,
} from "./use-standard-regions-query.ts";

interface Props {
    onChange: (regionCode: string) => void;
    selectedRegionCode: string | null | undefined;
}

const MAX_HEIGHT = 320;

// TODO(starin): For outside US, "State" could be localized (Province)
// TODO(starin): Add tooltip since "State" includes DC, territories, etc.
export default function RegionSelector({
    onChange,
    selectedRegionCode,
}: Props): JSX.Element {
    const standardRegionIDs = useRegionPickerQuery();
    const sortedRegionPickerOptions =
        getSortedRegionPickerOptions(standardRegionIDs);

    const regionOverrideResult = useRegionOverride();
    const publishAnalyticsEvent = usePublishAnalyticsEvent();

    return (
        <View style={{minBlockSize: "auto"}}>
            <LabelMedium tag="label" htmlFor="region-selector">
                {doNotTranslateYet`State`}
            </LabelMedium>
            <Strut size={spacing.small_12} />
            <SingleSelect
                id="region-selector"
                selectedValue={selectedRegionCode}
                onChange={(region) => {
                    onChange(region);
                    if (
                        region !== selectedRegionCode &&
                        regionOverrideResult.status === "success"
                    ) {
                        publishAnalyticsEvent({
                            eventType: "UserSettingsRegionChanged",
                            eventSchemaVersion: 1,
                            previousRegionCode: selectedRegionCode ?? "",
                            newRegionCode: region ?? "",
                            standardRegionUserContext_ipGeoRegionCode:
                                RequestInfo.kaRegion,
                            standardRegionUserContext_regionCodeSource:
                                regionOverrideResult.regionSource,
                            standardRegionUserContext_regionCodeUsed:
                                regionOverrideResult.regionCode,
                        });
                    }
                }}
                // i18n.doNotTranslateYet since the feature if 1st being rolled out en only
                placeholder={doNotTranslateYet`e.g., California`}
                style={styles.regionDropdown}
                dropdownStyle={styles.regionDropdownStyle}
                disabled={sortedRegionPickerOptions.length === 0}
            >
                {[
                    <OptionItem key="" value="" label="Automatic" />,
                    <SeparatorItem key="separator" />,
                    ...sortedRegionPickerOptions.map((standardRegion) => (
                        <OptionItem
                            key={standardRegion.id}
                            value={standardRegion.id}
                            label={standardRegion.label}
                        />
                    )),
                ]}
            </SingleSelect>
        </View>
    );
}

const styles = StyleSheet.create({
    regionDropdown: {
        width: "100%",
        maxInlineSize: 400,
        // HACK(yash): There are some inherited css properties on this page
        // which create a mismatch in expectations from wonder-blocks default
        // styling and what is rendered on the page. This attribute fixes that
        // by forcing the default wonder-blocks styles on the component.
        color: "initial",
    },
    regionDropdownStyle: {
        maxHeight: MAX_HEIGHT,
    },
});
